import { Grid, CircularProgress, Box, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { OASelectionV2, OATreeSelection, ObjetivoAprendizajeV2 } from "./types";
import ObjectiveCardV2 from "./ObjectiveCardV2";
import ObjectiveSelectModal from "./ObjectiveSelectModal";
import { get_objetivos_aprendizaje_tp } from "../../services/api";
import { useProyecto } from "../../data/ProyectoContext";
import { ObjetivoAprendizajeTPPayload } from "../../interfaces/Proyecto/iObjetivoAprendizajeTPPayload ";
import LogoCardSkeleton from "../Loading/LogoCardSkeleton";


type TpPayloadWithMeta = ObjetivoAprendizajeTPPayload & {
  asignaturaId: string;
  materia: string;
};

type SelectionByOA = Record<string, OASelectionV2 | undefined>;

interface Props {
  nivelId: string;
  asignaturaId: string;


  materiaNombre: string;

  promptData: Record<string, any>;
  setPromptData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
}

const GetObjetivosAprendizajeV2 = ({
  nivelId,
  asignaturaId,
  materiaNombre,
  promptData,
  setPromptData,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [activeOA, setActiveOA] = useState<ObjetivoAprendizajeV2 | null>(null);

  const [selectionByOA, setSelectionByOA] = useState<SelectionByOA>({});
  const [data, setData] = useState<ObjetivoAprendizajeV2[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>("");

  const { setProyecto } = useProyecto();


  const mapTreeToTpPayload = (tree: OATreeSelection): ObjetivoAprendizajeTPPayload => {
    return {
      objetivo: {
        id: String(tree?.objetivo?.id ?? ""),
        codigo: String(tree?.objetivo?.codigo ?? ""),
        texto: String(tree?.objetivo?.texto ?? ""),
      },
      aprendizajesEsperados: (tree?.aprendizajesEsperados ?? []).map((ae: any) => ({
        id: String(ae?.id ?? ""),
        texto: String(ae?.texto ?? ""),
        criteriosEvaluacion: (ae?.criteriosEvaluacion ?? []).map((ce: any) => ({
          id: String(ce?.id ?? ""),
          texto: String(ce?.texto ?? ""),
          objetivosGenericos: (ce?.objetivosGenericos ?? []).map((og: any) => ({
            id: String(og?.id ?? ""),
            texto: String(og?.texto ?? ""),
          })),
        })),
      })),
    };
  };


  const buildObjetivoAprendizajeTp = (map: SelectionByOA): TpPayloadWithMeta[] => {
    return Object.values(map)
      .filter(Boolean)
      .map((sel) => mapTreeToTpPayload(sel!.tree))
      .map((payload) => ({
        ...payload,
        asignaturaId,
        materia: materiaNombre,
      }));
  };


  const mergeByAsignaturaId = (prevArr: any[], asignaturaIdToMerge: string, nextArrForTab: any[]) => {
    const base = Array.isArray(prevArr) ? prevArr : [];
    const withoutThisTab = base.filter((item: any) => item?.asignaturaId !== asignaturaIdToMerge);
    return [...withoutThisTab, ...nextArrForTab];
  };


  useEffect(() => {
    const fetchObjetivos = async () => {
      setErrorMsg("");

      if (!nivelId || !asignaturaId) {
        setData([]);
        return;
      }

      setLoading(true);
      try {
        const resp = await get_objetivos_aprendizaje_tp(asignaturaId, nivelId);

        if (!resp || resp.status !== 200) {
          console.error("get_objetivos_aprendizaje_tp: respuesta inválida", resp);
          setData([]);
          setErrorMsg("No fue posible cargar los objetivos de aprendizaje.");
          return;
        }

        const raw: any[] =
          Array.isArray(resp.data?.data)
            ? resp.data.data
            : Array.isArray(resp.data)
              ? resp.data
              : Array.isArray(resp.data?.items)
                ? resp.data.items
                : [];

        const mapped: ObjetivoAprendizajeV2[] = raw
          .map((o: any) => ({
            id: String(o.id_objetivo_aprendizaje ?? o.id_objetivo ?? o.id ?? ""),
            codigo: String(o.code ?? o.codigo ?? ""),
            objetivo: String(o.description ?? o.objetivo ?? o.descripcion ?? o.name ?? ""),
            aprendizajesEsperados: Array.isArray(o.aprendizajes_esperados)
              ? o.aprendizajes_esperados
              : Array.isArray(o.aprendizajesEsperados)
                ? o.aprendizajesEsperados
                : [],
            criteriosEvaluacion: Array.isArray(o.criterios_evaluacion)
              ? o.criterios_evaluacion
              : Array.isArray(o.criteriosEvaluacion)
                ? o.criteriosEvaluacion
                : [],
            objetivosGenericos: Array.isArray(o.objetivos_genericos)
              ? o.objetivos_genericos
              : Array.isArray(o.objetivosGenericos)
                ? o.objetivosGenericos
                : [],
          }))
          .filter((x) => Boolean(x.id));

        setData(mapped);
      } catch (e) {
        console.error("Error consultando objetivos TP:", e);
        setData([]);
        setErrorMsg("Ocurrió un error consultando los objetivos de aprendizaje.");
      } finally {
        setLoading(false);
      }
    };

    fetchObjetivos();
    setSelectionByOA({});
    setOpen(false);
    setActiveOA(null);


  }, [nivelId, asignaturaId]);

  const sorted = useMemo(() => {
    const copy = [...data];
    copy.sort((a, b) => (a.codigo || "").localeCompare(b.codigo || ""));
    return copy;
  }, [data]);


  const handleOpenModal = (oa: ObjetivoAprendizajeV2) => {
    setActiveOA(oa);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setActiveOA(null);
  };

  const handleConfirm = (selection: OASelectionV2) => {
    setSelectionByOA((prev) => {
      const next: SelectionByOA = { ...prev, [selection.objetivoId]: selection };


      const objetivoAprendizajeTpForThisTab = buildObjetivoAprendizajeTp(next);


      setPromptData((prevDG) => {
        const prevArr = Array.isArray(prevDG?.objetivos_aprendizaje_tp)
          ? prevDG.objetivos_aprendizaje_tp
          : [];
        const merged = mergeByAsignaturaId(prevArr, asignaturaId, objetivoAprendizajeTpForThisTab);
        return { ...prevDG, objetivos_aprendizaje_tp: merged };
      });

      setPromptData((prevData: any) => ({
        ...prevData,

        objetivos_aprendizaje_cn: [
          {
            objetivo: "",
            tipo: "",
            indicadores: [],
          },
        ],
        objetivos_aprendizaje_org: [
          {
            objetivo: "",
            tipo: "",
            indicadores: [],
          },
        ],
      }));


      setProyecto((prevProyecto: any) => {
        const prevArr = Array.isArray(prevProyecto?.objetivos_aprendizaje_tp)
          ? prevProyecto.objetivos_aprendizaje_tp
          : [];
        const merged = mergeByAsignaturaId(prevArr, asignaturaId, objetivoAprendizajeTpForThisTab);
        return { ...prevProyecto, objetivos_aprendizaje_tp: merged };
      });

      setProyecto((prevData: any) => ({
        ...prevData,

        objetivos_aprendizaje_cn: [
          {
            objetivo: "",
            tipo: "",
            indicadores: [],
          },
        ],
        objetivos_aprendizaje_org: [
          {
            objetivo: "",
            tipo: "",
            indicadores: [],
          },
        ],
      }));

      return next;
    });

    handleClose();
  };

  const handleClearSelection = (objetivoId: string) => {
    setSelectionByOA((prev) => {
      const copy = { ...prev };
      delete copy[objetivoId];

      const objetivoAprendizajeTpForThisTab = buildObjetivoAprendizajeTp(copy);


      setPromptData((prevDG) => {
        const prevArr = Array.isArray(prevDG?.objetivo_aprendizaje)
          ? prevDG.objetivos_aprendizaje_tp
          : [];

        const withoutThisTab = prevArr.filter((item: any) => item?.asignaturaId !== asignaturaId);

        if (objetivoAprendizajeTpForThisTab.length === 0) {

          if (withoutThisTab.length === 0) {
            const nextDG = { ...prevDG };
            delete nextDG.objetivos_aprendizaje_tp;
            return nextDG;
          }
          return { ...prevDG, objetivos_aprendizaje_tp: withoutThisTab };
        }

        return {
          ...prevDG,
          objetivos_aprendizaje_tp: [...withoutThisTab, ...objetivoAprendizajeTpForThisTab],
        };
      });


      setProyecto((prevProyecto: any) => {
        const prevArr = Array.isArray(prevProyecto?.objetivos_aprendizaje_tp)
          ? prevProyecto.objetivos_aprendizaje_tp
          : [];

        const withoutThisTab = prevArr.filter((item: any) => item?.asignaturaId !== asignaturaId);

        if (objetivoAprendizajeTpForThisTab.length === 0) {
          if (withoutThisTab.length === 0) {
            const nextProyecto = { ...prevProyecto };
            delete nextProyecto.objetivos_aprendizaje_tp;
            return nextProyecto;
          }
          return { ...prevProyecto, objetivos_aprendizaje_tp: withoutThisTab };
        }

        return {
          ...prevProyecto,
          objetivos_aprendizaje_tp: [...withoutThisTab, ...objetivoAprendizajeTpForThisTab],
        };
      });

      return copy;
    });

    if (activeOA?.id === objetivoId) handleClose();
  };


  if (!nivelId || !asignaturaId) {
    return (
      <Box px={2} py={2}>
        <Typography sx={{ fontFamily: "Montserrat", color: "#336B87" }}>
          Selecciona una especialidad, un nivel y una asignatura para ver los objetivos.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Grid display={"flex"} justifyContent={"center"}>

        <LogoCardSkeleton />

      </Grid>
    );
  }

  if (errorMsg) {
    return (
      <Box px={2} py={2}>
        <Typography
          sx={{
            fontFamily: "Montserrat",
            color: "#BA4534",
            fontWeight: 700,
          }}
        >
          {errorMsg}
        </Typography>
      </Box>
    );
  }

  if (sorted.length === 0) {
    return (
      <Box px={2} py={2}>
        <Typography sx={{ fontFamily: "Montserrat", color: "#336B87" }}>
          No se encontraron objetivos para la selección actual.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Grid container spacing={3} m={4} maxWidth="90%">
        {sorted.map((oa) => (
          <ObjectiveCardV2
            key={oa.id}
            objetivo={oa}
            selection={selectionByOA[oa.id]}
            onOpenModal={() => handleOpenModal(oa)}
            onClearSelection={() => handleClearSelection(oa.id)}
          />
        ))}
      </Grid>

      {activeOA && (
        <ObjectiveSelectModal
          open={open}
          objetivo={activeOA}
          initialSelection={selectionByOA[activeOA.id]}
          onClose={handleClose}
          onConfirm={handleConfirm}
        />
      )}
    </>
  );
};

export default GetObjetivosAprendizajeV2;
